import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";

import EcoPagination from "../../../components/eco-ui/EcoPagination";
import EcoPageHeader from "../../../components/eco-page-header/EcoPageHeader";
import { useGoBack } from "../../../hooks/useGoBack";
import type { RootState } from "../../../store/store";
import DocumentFileButton from "../components/DocumentFileButton";
import ReasonModal from "../components/ReasonModal";
import { getStoreScheduleSummaryApi } from "../api/scheduleApi";
import {
  ADJUSTMENT_REASON_LABELS,
  ADJUSTMENT_STATUS_BADGE,
  ADJUSTMENT_STATUS_LABELS,
  ADJUSTMENT_TYPE_LABELS,
  AdjustmentReason,
  AdjustmentStatus,
  approveAdjustmentApi,
  approveManyAdjustmentsApi,
  DP_ADJUSTMENT_REASONS,
  DpQueue,
  DpQueueItem,
  duration,
  fetchAdjustmentFileApi,
  hhmm,
  listAdjustmentsApi,
  rejectAdjustmentApi,
  timeOf,
} from "../api/timekeepingApi";
import { DP_PAGE, DP_TIMESHEET_PAGE, DP_TIMESHEETS_PAGE } from "../routes";
import "./Dp.css";
import "./DpEco.css";
import "./Timekeeping.css";

const PAGE_SIZE = 30;
const weekdayDate = (iso: string) => new Date(iso).toLocaleDateString("pt-BR", { timeZone: "UTC", weekday: "short", day: "2-digit", month: "2-digit" });

/** O pedido ou o lote em uma linha. */
function describe(group: DpQueueItem[]) {
  const first = group[0];
  if (first.type === "INCLUSAO_MARCACAO") return `Incluir ${group.filter((a) => a.proposedAt).map((a) => timeOf(a.proposedAt as string)).join(", ")}`;
  if (first.type === "DESCONSIDERAR_MARCACAO") return `Desconsiderar ${first.targetPunch ? timeOf(first.targetPunch.punchedAt) : "marcação"}`;
  if (first.minutes) return `${ADJUSTMENT_TYPE_LABELS[first.type]} (${duration(first.minutes)})`;
  return ADJUSTMENT_TYPE_LABELS[first.type];
}

/**
 * Fila de pedidos de correção do ponto de todas as lojas, com o dia no espelho
 * ao lado para decidir sem abrir outra tela. Lote (dia inteiro) aparece num
 * cartão só; a seleção aprova vários de uma vez.
 */
export default function AdjustmentsQueuePage() {
  const navigate = useNavigate();
  const goBack = useGoBack(DP_TIMESHEETS_PAGE());
  const token = useSelector((state: RootState) => state.auth.accessToken);

  const [status, setStatus] = useState<AdjustmentStatus | "">("PENDENTE");
  const [storeId, setStoreId] = useState("");
  const [reasonCode, setReasonCode] = useState<AdjustmentReason | "">("");
  const [q, setQ] = useState("");
  const [page, setPage] = useState(1);
  const [stores, setStores] = useState<Array<{ storeId: string; storeName: string }>>([]);
  const [data, setData] = useState<DpQueue | null>(null);
  const [loading, setLoading] = useState(false);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [busy, setBusy] = useState(false);
  const [rejecting, setRejecting] = useState<DpQueueItem[] | null>(null);

  useEffect(() => {
    if (!token) return;
    getStoreScheduleSummaryApi(token)
      .then((s) => setStores(s.stores.map(({ storeId: id, storeName }) => ({ storeId: id, storeName }))))
      .catch(() => setStores([]));
  }, [token]);

  const load = useCallback(async () => {
    if (!token) return;
    setLoading(true);
    try {
      setData(
        await listAdjustmentsApi(token, {
          status: status || undefined,
          storeId: storeId || undefined,
          reasonCode: reasonCode || undefined,
          q: q.trim() || undefined,
          page,
          limit: PAGE_SIZE,
        }),
      );
      setSelected(new Set());
    } catch (error: any) {
      toast.error(error?.message || "Erro ao carregar os ajustes.");
    } finally {
      setLoading(false);
    }
  }, [token, status, storeId, reasonCode, q, page]);

  useEffect(() => {
    const id = setTimeout(load, q ? 350 : 0);
    return () => clearTimeout(id);
  }, [load, q]);

  const groups = useMemo(() => {
    const map = new Map<string, DpQueueItem[]>();
    for (const item of data?.items ?? []) {
      const key = item.batchId ? `${item.batchId}|${item.status}` : item.id;
      map.set(key, [...(map.get(key) ?? []), item]);
    }
    return Array.from(map.entries());
  }, [data]);

  const ids = (group: DpQueueItem[]) => group.filter((a) => a.status === "PENDENTE").map((a) => a.id);
  const isSelected = (group: DpQueueItem[]) => ids(group).length > 0 && ids(group).every((id) => selected.has(id));
  const toggle = (group: DpQueueItem[]) =>
    setSelected((s) => {
      const next = new Set(s);
      const on = isSelected(group);
      ids(group).forEach((id) => (on ? next.delete(id) : next.add(id)));
      return next;
    });

  const approve = async (list: string[]) => {
    if (!token || !list.length) return;
    setBusy(true);
    try {
      if (list.length === 1) {
        await approveAdjustmentApi(token, list[0]);
        toast.success("Pedido aprovado.");
      } else {
        const res = await approveManyAdjustmentsApi(token, list);
        toast.success(`${res.approved} aprovado(s).`);
        if (res.failed.length) toast.warn(`${res.failed.length} não puderam ser aprovados: ${res.failed[0].message}`);
      }
      await load();
    } catch (error: any) {
      toast.error(error?.message || "Erro ao aprovar.");
    } finally {
      setBusy(false);
    }
  };

  const maxReason = Math.max(1, ...(data?.byReason ?? []).map((r) => r.count));

  if (!token) return null;

  return (
    <div className="dpLayout">
      <main className="dpMain">
        <div className="dpContainer">
          <EcoPageHeader
            crumbs={[
              { label: "Departamento Pessoal", onClick: () => navigate(DP_PAGE()) },
              { label: "Ponto", onClick: () => navigate(DP_TIMESHEETS_PAGE()) },
              { label: "Fila de ajustes" },
            ]}
            title="Fila de ajustes do ponto"
            subtitle="Pedidos de correção dos colaboradores e lançamentos do DP. Recusar exige um motivo, que o colaborador vê no portal."
            onBack={goBack}
            backLabel="Voltar ao ponto"
          />

          <div className="dpControls">
            <div className="dpFormGroup" style={{ margin: 0 }}>
              <label htmlFor="qStatus">Situação</label>
              <select id="qStatus" className="dpSelect" value={status} onChange={(e) => { setStatus(e.target.value as AdjustmentStatus | ""); setPage(1); }}>
                <option value="">Todas</option>
                {(Object.keys(ADJUSTMENT_STATUS_LABELS) as AdjustmentStatus[]).map((s) => (
                  <option key={s} value={s}>
                    {ADJUSTMENT_STATUS_LABELS[s]}
                  </option>
                ))}
              </select>
            </div>
            <div className="dpFormGroup" style={{ margin: 0 }}>
              <label htmlFor="qStore">Loja</label>
              <select id="qStore" className="dpSelect" value={storeId} onChange={(e) => { setStoreId(e.target.value); setPage(1); }}>
                <option value="">Todas</option>
                {stores.map((s) => (
                  <option key={s.storeId} value={s.storeId}>
                    {s.storeName}
                  </option>
                ))}
              </select>
            </div>
            <div className="dpFormGroup" style={{ margin: 0 }}>
              <label htmlFor="qReason">Motivo</label>
              <select id="qReason" className="dpSelect" value={reasonCode} onChange={(e) => { setReasonCode(e.target.value as AdjustmentReason | ""); setPage(1); }}>
                <option value="">Todos</option>
                {DP_ADJUSTMENT_REASONS.map((r) => (
                  <option key={r} value={r}>
                    {ADJUSTMENT_REASON_LABELS[r]}
                  </option>
                ))}
              </select>
            </div>
            <div className="dpFormGroup" style={{ margin: 0 }}>
              <label htmlFor="qSearch">Colaborador</label>
              <input id="qSearch" className="dpInput" placeholder="Nome ou CPF" value={q} onChange={(e) => { setQ(e.target.value); setPage(1); }} />
            </div>
          </div>

          <div className="dpTkLayout">
            <div>
              <div className="dpControls">
                <button type="button" className="dpBtn dpBtn--primary dpBtn--small" disabled={!selected.size || busy} onClick={() => approve(Array.from(selected))}>
                  Aprovar selecionados ({selected.size})
                </button>
                <span className="dpMuted">{loading ? "Carregando..." : `${data?.total ?? 0} ajuste(s)`}</span>
              </div>

              <div className="dpTkQueue">
                {groups.map(([key, group]) => {
                  const first = group[0];
                  const ctx = first.dayContext;
                  const pendingIds = ids(group);
                  return (
                    <article key={key} className="dpTkQueueItem">
                      <input type="checkbox" aria-label="Selecionar" disabled={!pendingIds.length} checked={isSelected(group)} onChange={() => toggle(group)} />
                      <div className="dpTkQueueItem__body">
                        <strong>
                          {first.employee.socialName || first.employee.fullName} · {weekdayDate(first.day)}
                        </strong>
                        <small>
                          {first.store?.nomeFantasia ?? "Sem loja"} · {ADJUSTMENT_REASON_LABELS[first.reasonCode]} ·{" "}
                          {first.requestedByEmployee ? "pedido do colaborador" : `lançado por ${first.requestedByUser?.nomeCompleto ?? "DP"}`} · {new Date(first.createdAt).toLocaleDateString("pt-BR")}
                        </small>
                        <span>
                          {describe(group)}{" "}
                          <span className={`dpBadge ${first.annulledBy ? "dpBadge--gray" : ADJUSTMENT_STATUS_BADGE[first.status]}`}>
                            {first.annulledBy ? "Anulado" : ADJUSTMENT_STATUS_LABELS[first.status]}
                          </span>
                        </span>
                        {first.reason ? <span>“{first.reason}”</span> : null}
                        {ctx ? (
                          <small>
                            No espelho: {ctx.startMinute !== null ? `previsto ${hhmm(ctx.startMinute)}–${hhmm(ctx.endMinute)}` : `previsto ${duration(ctx.expectedMinutes)}`} · marcado{" "}
                            {ctx.effectivePunches.length ? ctx.effectivePunches.map((p) => `${timeOf(p.at)}${p.source === "AJUSTE" ? "*" : ""}`).join(", ") : "nada"}
                            {ctx.timesheetStatus === "FECHADO" ? " · espelho fechado" : ""}
                          </small>
                        ) : null}
                        {first.decisionNote ? <small>Decisão: {first.decisionNote}</small> : null}
                      </div>
                      <div className="dpTkActions">
                        {first.attachmentAssetId ? (
                          <DocumentFileButton token={token} fetchBlob={() => fetchAdjustmentFileApi(token, first.id)} label="Anexo" className="dpBtn dpBtn--secondary dpBtn--small" />
                        ) : null}
                        {pendingIds.length ? (
                          <>
                            <button type="button" className="dpBtn dpBtn--primary dpBtn--small" disabled={busy} onClick={() => approve(pendingIds)}>
                              Aprovar
                            </button>
                            <button type="button" className="dpBtn dpBtn--danger dpBtn--small" disabled={busy} onClick={() => setRejecting(group.filter((a) => a.status === "PENDENTE"))}>
                              Recusar
                            </button>
                          </>
                        ) : null}
                        <button
                          type="button"
                          className="dpBtn dpBtn--secondary dpBtn--small"
                          onClick={() => {
                            const d = new Date(first.day);
                            navigate(DP_TIMESHEET_PAGE(first.contractId, d.getUTCFullYear(), d.getUTCMonth() + 1));
                          }}
                        >
                          Espelho
                        </button>
                      </div>
                    </article>
                  );
                })}
                {!groups.length && !loading ? <div className="dpEmpty">Nenhum ajuste com esses filtros.</div> : null}
              </div>

              {data && data.total > PAGE_SIZE ? <EcoPagination page={page} pageSize={PAGE_SIZE} total={data.total} onChange={setPage} disabled={loading} /> : null}
            </div>

            <aside className="dpTkSide">
              <h3>Causas</h3>
              <span className="dpMuted">Motivos dos ajustes com os filtros atuais. Muito "celular indisponível" ou "sem internet" numa loja aponta problema de conexão ou de aparelho.</span>
              <div className="dpTkBars">
                {(data?.byReason ?? [])
                  .slice()
                  .sort((a, b) => b.count - a.count)
                  .map((r) => (
                    <div key={r.reasonCode} className="dpTkBar">
                      <span>{ADJUSTMENT_REASON_LABELS[r.reasonCode]}</span>
                      <span className="dpTkBar__track">
                        <span className="dpTkBar__fill" style={{ width: `${(r.count / maxReason) * 100}%`, display: "block" }} />
                      </span>
                      <strong className="dpTkNum">{r.count}</strong>
                    </div>
                  ))}
                {!data?.byReason.length ? <span className="dpMuted">Sem dados.</span> : null}
              </div>
            </aside>
          </div>
        </div>
      </main>

      {rejecting ? (
        <ReasonModal
          title={rejecting.length > 1 ? `Recusar o pedido (${rejecting.length} marcações)` : "Recusar o pedido"}
          description="O colaborador vê este motivo no portal."
          confirmLabel="Recusar"
          onClose={() => setRejecting(null)}
          onConfirm={async (reason) => {
            if (reason.length < 5) throw new Error("Explique o motivo (mínimo de 5 caracteres).");
            for (const a of rejecting) await rejectAdjustmentApi(token, a.id, reason);
            setRejecting(null);
            toast.success("Pedido recusado.");
            await load();
          }}
        />
      ) : null}
    </div>
  );
}
